import React from 'react'
import { Outlet } from 'react-router-dom'
import { Box, CircularProgress, Typography, Button } from '@mui/material'
import { useGetUserQuery } from 'graphql/generated/graphql'

/**
 * AuthorizedRoute checks authentication by making a GraphQL query.
 * If the query returns user data, the user is authenticated.
 * If it fails, show a login prompt.
 */
export default function AuthorizedRoute(): JSX.Element {
  const { data, loading, error, refetch } = useGetUserQuery({
    fetchPolicy: 'network-only',
  })

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#0f172a' }}>
        <CircularProgress sx={{ color: '#a855f7', mb: 2 }} />
        <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>Loading...</Typography>
      </Box>
    )
  }

  // User is authenticated
  if (data?.user) {
    return <Outlet />
  }

  // Not authenticated — show login prompt
  // We can't auto-redirect because we don't know if the backend will
  // redirect to Cognito or if there's a session issue. Let the user click.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#0f172a', gap: 2 }}>
      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '24px' }}>
        nalapod
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 2 }}>
        Sign in to access your dashboard
      </Typography>
      <Button
        href="/api/auth-redirect"
        sx={{
          background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
          color: 'white',
          borderRadius: '12px',
          textTransform: 'none',
          px: 4,
          py: 1.2,
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Sign In
      </Button>
      <Button
        onClick={() => { refetch().catch(console.error) }}
        sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', fontSize: '13px' }}
      >
        Already logged in? Click to retry
      </Button>
    </Box>
  )
}
